import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel, Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import { CallRoute, CallQueue } from '../../../interfaces/calls';
import queuesService from '../../../services/appointments/queues';
import { CustomTable } from '../../common/CustomTable';

type Props = {
  breadcrumbs: ReactNode;
};

export const QueuesCenter = ({ breadcrumbs }: Props) => {
  const [routes, setRoutes] = useState<CallRoute[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<CallRoute | null>(null);
  
  // Form State
  const [routeName, setRouteName] = useState('');
  const [routeDesc, setRouteDesc] = useState('');
  const [queues, setQueues] = useState<CallQueue[]>([]);

  const notify = useNotify();

  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await queuesService.getAllRoutes();
      if (response.success && response.data) {
        setRoutes(response.data);
      } else {
        notify('Failed to fetch routes', { type: 'error' });
      }
    } catch (error) {
      notify('Failed to fetch routes', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const handleOpenModal = async (route?: CallRoute) => {
    if (route) {
      // Fetch full details including queues
      try {
        const fullRouteRes = await queuesService.getRoute(route.id);
        if (fullRouteRes.success && fullRouteRes.data) {
            const data = fullRouteRes.data;
            setEditingRoute(data);
            setRouteName(data.displayName);
            setRouteDesc(data.description || '');
            setQueues(data.callQueues || []);
        }
      } catch (e) {
         notify('Error loading details', { type: 'error' });
         return;
      }
    } else {
      setEditingRoute(null);
      setRouteName('');
      setRouteDesc('');
      setQueues([]);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRoute(null);
  };

  const handleAddQueue = () => {
    setQueues([...queues, {
        displayName: 'New Queue',
        ivrDigit: '',
        priority: 1,
        maxWaitTime: 300,
        allowVoicemail: true,
        isActive: true,
    }]);
  };

  const handleQueueChange = (index: number, field: keyof CallQueue, value: any) => {
    const newQueues = [...queues];
    newQueues[index] = { ...newQueues[index], [field]: value };
    setQueues(newQueues);
  };

  const handleRemoveQueue = (index: number) => {
    const newQueues = [...queues];
    newQueues.splice(index, 1);
    setQueues(newQueues);
  };

  const handleSave = async () => {
    try {
      const payload = {
        displayName: routeName,
        description: routeDesc,
        queues: queues,
        // TODO: Add other fields (business hours, etc.)
      };

      if (editingRoute) {
        await queuesService.updateRoute(editingRoute.id, payload);
        notify('Route updated successfully', { type: 'success' });
      } else {
        await queuesService.createRoute(payload);
        notify('Route created successfully', { type: 'success' });
      }
      handleCloseModal();
      fetchRoutes();
    } catch (error) {
      notify('Failed to save route', { type: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this route? It will delete all associated queues.')) return;
    try {
      await queuesService.deleteRoute(id);
      notify('Route deleted successfully', { type: 'success' });
      fetchRoutes();
    } catch (error) {
      notify('Failed to delete route', { type: 'error' });
    }
  };

  const rows = useMemo(
    () =>
      routes.map((route) => ({
        name: (
            <Box>
                <Typography variant='body2' fontWeight="bold">{route.displayName}</Typography>
                <Typography variant='caption'>{route.description}</Typography>
            </Box>
        ),
        queues: (
            <Box display="flex" gap={0.5} flexWrap="wrap">
                {route.callQueues?.map(q => (
                    <Box key={q.id} border="1px solid #eee" borderRadius="4px" px={1} bgcolor="#f9f9f9">
                        <Typography variant="caption">{q.ivrDigit ? `[${q.ivrDigit}] ` : ''}{q.displayName}</Typography>
                    </Box>
                ))}
            </Box>
        ),
        actions: (
            <Box>
                <IconButton size='small' onClick={() => handleOpenModal(route)}>
                    <EditIcon fontSize='small' />
                </IconButton>
                <IconButton size='small' onClick={() => handleDelete(route.id)}>
                    <DeleteIcon fontSize='small' />
                </IconButton>
            </Box>
        ),
      })),
    [routes],
  );

  return (
    <Box className='tab-section' marginTop={0}>
      <Box className='agents-center-header'>
        <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom='16px'>
          {breadcrumbs}
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Create Route
          </Button>
        </Box>
      </Box>

      <CustomTable
        columns={[
          { key: 'name', label: 'Route Name', width: '250px' },
          { key: 'queues', label: 'Queues' },
          { key: 'actions', label: 'Actions', width: '100px' },
        ]}
        defaultText='No routes found'
        key='queues-center-table'
        loading={loading}
        rows={rows}
        pagination={{
            rowsPerPageOptions: [100],
            rowPerPage: 100,
            page: 1,
            totalCount: routes.length,
            onPageChange: () => {},
        }}
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth='md' fullWidth>
        <DialogTitle>{editingRoute ? 'Edit Route' : 'Create Route'}</DialogTitle>
        <DialogContent>
            <Box mt={2} display="flex" flexDirection="column" gap={2}>
                <TextField
                    fullWidth
                    label="Route Name"
                    value={routeName}
                    onChange={(e) => setRouteName(e.target.value)}
                />
                <TextField
                    fullWidth
                    label="Description"
                    value={routeDesc}
                    onChange={(e) => setRouteDesc(e.target.value)}
                />
                
                <Typography variant="subtitle1" mt={2}>Queues</Typography>
                {queues.map((queue, index) => (
                    <Accordion key={index} variant="outlined">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>{queue.ivrDigit ? `[Digit ${queue.ivrDigit}] ` : ''}{queue.displayName}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={8}>
                                    <TextField
                                        fullWidth size="small"
                                        label="Queue Name"
                                        value={queue.displayName}
                                        onChange={(e) => handleQueueChange(index, 'displayName', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth size="small"
                                        label="IVR Digit"
                                        value={queue.ivrDigit || ''}
                                        onChange={(e) => handleQueueChange(index, 'ivrDigit', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth size="small" type="number"
                                        label="Max Wait Time (sec)"
                                        value={queue.maxWaitTime}
                                        onChange={(e) => handleQueueChange(index, 'maxWaitTime', Number(e.target.value))}
                                    />
                                </Grid>
                                <Grid item xs={6} display="flex" alignItems="center">
                                    <FormControlLabel
                                        control={<Switch checked={queue.allowVoicemail} onChange={(e) => handleQueueChange(index, 'allowVoicemail', e.target.checked)} />}
                                        label="Allow Voicemail"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                     <Button size="small" color="error" onClick={() => handleRemoveQueue(index)}>Remove Queue</Button>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}
                
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddQueue}>Add Queue</Button>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={!routeName.trim()}>
                Save
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
